import { getErrorMessage, logMessage, } from '../utils/helpers';
import { HistoricalData,RequestParams,PeriodParams,BarData,Meta,Bars } from "../models/historical"
import { ResolveSymbolInfo } from '../models/symbolData';
import { trace, context, propagation, SpanStatusCode } from "@opentelemetry/api";
const tracer = trace.getTracer("inhouse-request-tracer");
export class HistoryProvider {
    private _datafeedUrl:string
    private _requester:any
    private _wantsOi:() => boolean
    /**
     * `wantsOi` answers, per request, whether anything on the chart reads open
     * interest — see `needs_oi` in getBars. Omitted means no, so a provider
     * built without it asks for nothing.
     */
    constructor(datafeedUrl:string, requester:any, wantsOi:(() => boolean) | null = null) {
        this._datafeedUrl = datafeedUrl;
        this._requester = requester;
        this._wantsOi = wantsOi || (() => false);
    }

    //This function returns the historical data of candles 
    getBars(symbolInfo:ResolveSymbolInfo, resolution:string, periodParams:PeriodParams) {
        return tracer.startActiveSpan("GetBars", async (span) => {
            span.setAttributes({
                symbol: symbolInfo.symbol,
                resolution: resolution,
            })
            // Cap countback so the backend never has to serve more than MAX_BARS bars
            // per call. TradingView's UDF flow can request very large countback values on
            // first load (large screens, deep history, resolution switches), which inflates
            // chart-load latency; capping bounds it and TradingView auto-paginates older
            // history with later getBars calls. `from` is left untouched so the server's
            // session-aware logic fills bars correctly across non-trading hours (clamping
            // `from` client-side leaves visible gaps, e.g. 9:15–10:00).
            const MAX_BARS = 1000;
            let countBack = periodParams.countBack || MAX_BARS;
            if (countBack > MAX_BARS) {
                countBack = MAX_BARS;
            }
            const requestParams:RequestParams = {
                symbol: String(symbolInfo.symbol),
                token: String(symbolInfo.odin_token),
                market_segment_id: Number(symbolInfo.odin_market_segment_id),
                resolution: resolution,
                from: periodParams.from,
                to: periodParams.to,
                first_data_request: periodParams.firstDataRequest,
                next_time: true,
                countback: countBack,
                // ADVA-1013 (P7): this build overlays index volume live-side, so
                // the backend may return real (futures-sourced) volume for index
                // history. Builds that omit this get index volume masked to 0.
                supports_index_volume: true,
                // The open-interest columns. `needs_oi`, not the `supports_*`
                // spelling its neighbour uses — that is the name the backend
                // shipped, and sending the wrong one is silent.
                //
                // ASKED FOR ON DEMAND. Until 2026-09-10 this was a flat `false`
                // because the build serving oi_c was deployed nowhere, and the
                // alternative — every chart of every symbol asking for a column
                // none of them could use — was worse. What retires that is
                // asking only where something reads the answer: `wantsOi` is
                // true exactly while one of the studies in vendor/oi-studies.js
                // is on the layout.
                //
                // EVALUATED PER REQUEST, not captured when the provider was
                // built. Unlike supports_index_volume above this is not a
                // property of the build — it changes the moment a study is added
                // or removed, and the next history page has to carry the new
                // answer or that study plots nothing until a reload.
                //
                // NOT GATED ON RESOLUTION, and the pack's daily/weekly warm-up
                // is what makes that visible: it borrows this same provider
                // instance, so while an OI study is on the chart its 1D/1W pages
                // carry needs_oi too, for a column CPR never reads. That costs a
                // few unread columns on a request that was happening anyway. The
                // alternative — "OI is intraday-only, so gate on the resolution"
                // — is a claim that lives in the pack's prose and in no line of
                // code, and coding it would silently keep OI off the day the
                // backend starts serving it on daily bars.
                //
                // Sent as false rather than dropped: every environment has been
                // receiving this key since OI shipped, so changing its value
                // cannot surprise a backend that has already proven it tolerates
                // the key, while removing it is a request shape none of them has
                // seen since before the feature.
                needs_oi: !!this._wantsOi(),
            };
            if (symbolInfo.currency_code) {
                requestParams.currencyCode = symbolInfo.currency_code;
            }
            if (symbolInfo.unit_id) {
                requestParams.unitId = symbolInfo.unit_id;
            }
            if (requestParams.token == undefined || requestParams.token == 0 || requestParams.token == null || requestParams.token == "" || requestParams.token == "0") {
                return new Promise((resolve, reject) => {
                    const meta:Meta = {
                        noData :true 
                    }
                    let data:Bars={
                        bars: [],
                        meta: meta,
                    }
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.end()
                    resolve(data);
                });
            }
            const parseResponse = (response:HistoricalData):Bars => {
                const bars:BarData[] = [];
                const meta:Meta = {
                    noData: false,
                    nextTime:0,
                };
                if (response.s === 'no_data') {
                    meta.noData = true;
                    meta.nextTime = response.next_time;
                }
                else if((response.s as string) === "error"){
                    meta.noData = true;
                }
                else {
                    const volumePresent = response.v !== undefined;
                    const ohlPresent = response.o !== undefined;
                    // Open Interest arrives as OHLC per bar: oi_o/oi_h/oi_l/oi_c.
                    // We read oi_c and nothing else — OI at the bar's close is
                    // what every OI study is defined against, and on the
                    // deployed data the other three are derived from it
                    // (oi_h === max(oi_o, oi_c), oi_l === min(oi_o, oi_c),
                    // oi_o[n] === oi_c[n-1]). `oi` is accepted as a fallback for
                    // a backend sending the single-column shape.
                    const oiSeries: number[] | null = Array.isArray(response.oi_c)
                        ? response.oi_c
                        : (Array.isArray(response.oi) ? response.oi : null);
                    var totalLastDayVolumeTillBar = 0
                    if(response.t && response.c && response.o && response.h && response.l && response.v){
                        for (let i = 0; i < response.t?.length; ++i) {
                            const barValue:BarData = {
                                time: response.t[i] * 1000,
                                close: Number(response.c[i]),
                                open: Number(response.c[i]),
                                high: Number(response.c[i]),
                                low: Number(response.c[i]),
                                volume:0,
                                totalLastDayVolumeTillBar:0
                            };
                            if (ohlPresent) {
                                barValue.open = Number(response.o[i]);
                                barValue.high = Number(response.h[i]);
                                barValue.low = Number(response.l[i]);
                            }
                            if (volumePresent) {
                                barValue.volume = Number(response.v[i]);
                                if( barValue.time >= bod()){
                                    totalLastDayVolumeTillBar = totalLastDayVolumeTillBar  + barValue.volume
                                }

                            }
                            if( resolution != "1D" && resolution != "1W" && resolution != "1M" &&resolution != "1Y"){
                                barValue.totalLastDayVolumeTillBar = totalLastDayVolumeTillBar
                            }
                            if (oiSeries) {
                                // parseFloat, NOT Number. Number(null) is 0 and
                                // Number('') is 0, both finite — so a gap in the
                                // column would be written as real open interest
                                // of zero, which every OI study would read as a
                                // total unwind. parseFloat gives NaN for both,
                                // and the bar simply carries no oi key.
                                const oi = parseFloat(oiSeries[i] as unknown as string);
                                if (isFinite(oi)) {
                                    barValue.oi = oi;
                                }
                            }
                            bars.push(barValue);
                        }
                    }
                }
                // Server 1S history can carry out-of-order stragglers at its
                // live edge (persisted-store vs realtime-buffer seam) — v32's
                // transport validates every array and logs "time order
                // violation" (observed live 2026-07-20: ...10:07:50 →
                // 10:07:45 inside ONE response). Enforce strictly-increasing
                // times; dropped stragglers are counted in
                // window.__tvDroppedHistBars (they're rebuilt by the stream).
                if (bars.length > 1) {
                    let prevTime = -Infinity;
                    let writeIdx = 0;
                    for (let i = 0; i < bars.length; ++i) {
                        if (bars[i].time > prevTime) {
                            prevTime = bars[i].time;
                            bars[writeIdx++] = bars[i];
                        }
                    }
                    if (writeIdx < bars.length) {
                        const w = window as any;
                        w.__tvDroppedHistBars = (w.__tvDroppedHistBars || 0) + (bars.length - writeIdx);
                        bars.length = writeIdx;
                    }
                }
                return { bars: bars, meta: meta };
            };
            const requestWindow = (fromSec:number):Promise<Bars> => {
                return this._requester.sendRequest(this._datafeedUrl, 'data/history', { ...requestParams, from: fromSec })
                    .then((response:HistoricalData) => {
                        if (response.s !== 'ok' && response.s !== 'no_data') {
                            span.setStatus({ code: SpanStatusCode.ERROR });
                            return Promise.reject(response.s);
                        }
                        return parseResponse(response);
                    });
            };
            return new Promise((resolve, reject) => {
                (async () => {
                    let data = await requestWindow(periodParams.from);
                    // Seconds resolutions honor TV's countBack contract here
                    // instead of letting TV paginate: the server fills the
                    // [from,to] window only, and on a sparse symbol a 1S window
                    // holds far fewer bars than countBack — TV then walks
                    // backwards with ~10 SERIAL getBars calls (~1.5s on prod,
                    // minutes on a slow backend). Widening the window ourselves
                    // (×5, ×25; ≤2 extra calls, ≤3 days back) satisfies
                    // countBack in one getBars round-trip. Minute charts don't
                    // need this — they fetch server-native aggregations now.
                    const isSeconds = /S$/.test(resolution);
                    if (isSeconds && !data.meta.noData) {
                        const spanSec = Math.max(periodParams.to - periodParams.from, 60);
                        for (let widen = 1; widen <= 2 && data.bars.length < countBack; ++widen) {
                            const newFrom = Math.max(periodParams.to - spanSec * Math.pow(5, widen), periodParams.to - 3 * 86400);
                            if (newFrom >= periodParams.from) { break; }
                            const wider = await requestWindow(newFrom);
                            if (wider.meta.noData) { break; }
                            if (wider.bars.length > data.bars.length) { data = wider; }
                            else { break; }
                        }
                    }
                    return data;
                })().then((data) => {
                    span.end()
                    resolve(data);
                }).catch((reason:any) => {
                    // tslint:disable-next-line:no-console
                    context.with(trace.setSpan(context.active(), span), () => {
                        console.error("Error parsing response:", reason);
                    });
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.end()
                    logMessage(`HistoryProvider: getBars() failed, error=${getErrorMessage(reason)}`);
                    reject(reason);
                });
            });
        })
        
       
        
    }
}

function bod(){ //It returns the date and time of current day
    var now = new Date();
    var startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var timestamp = startOfDay;
    return Number(timestamp)
}
